import { useState } from "react";
import { ExternalLink, Chrome, Play, Lock, Radio } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// neon accents per spec: Netflix red, Prime cyan, Disney pink, Hulu purple,
// Max cyan, Tubi pink, Pluto purple, YouTube red (+ Peacock neon green)
const SERVICES = [
  { key: "netflix", name: "Netflix", type: "paid", home: "https://www.netflix.com", neon: "#FF0040", mono: "N" },
  { key: "prime", name: "Prime Video", type: "paid", home: "https://www.primevideo.com", neon: "#00D9FF", mono: "prime" },
  { key: "disney", name: "Disney+", type: "paid", home: "https://www.disneyplus.com", neon: "#FF006E", mono: "D+" },
  { key: "hulu", name: "Hulu", type: "paid", home: "https://www.hulu.com", neon: "#9D4EDD", mono: "hulu" },
  { key: "max", name: "Max", type: "paid", home: "https://www.max.com", neon: "#00D9FF", mono: "MAX" },
  { key: "peacock", name: "Peacock", type: "paid", home: "https://www.peacocktv.com", neon: "#00FF66", mono: "P" },
  { key: "tubi", name: "Tubi", type: "free", home: "https://tubitv.com", neon: "#FF006E", mono: "tubi" },
  { key: "plutotv", name: "Pluto TV", type: "free", home: "https://pluto.tv", neon: "#9D4EDD", mono: "pluto" },
  { key: "youtube", name: "YouTube", type: "free", home: "", neon: "#FF0040", mono: "▶" },
];

const NEEDS_EXTENSION = new Set(["netflix", "prime", "disney", "hulu", "max", "peacock"]);

function Tile({ s, onClick }) {
  const wide = s.mono.length > 2;
  return (
    <button
      data-testid={`service-${s.key}`}
      onClick={() => onClick(s)}
      className="svc-tile group relative flex flex-col items-center justify-center gap-3 rounded-2xl p-5 bg-[#0A0E27]/70 backdrop-blur-md"
      style={{ border: `1px solid ${s.neon}66`, boxShadow: `0 0 18px ${s.neon}22` }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 34px ${s.neon}88`; e.currentTarget.style.borderColor = s.neon; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 18px ${s.neon}22`; e.currentTarget.style.borderColor = `${s.neon}66`; }}
    >
      <span
        className="svc-glyph flex items-center justify-center rounded-xl font-display font-black lowercase"
        style={{
          width: 56, height: 56, background: `${s.neon}14`, color: s.neon,
          fontSize: wide ? 15 : 26, textTransform: wide ? "lowercase" : "none",
          border: `1px solid ${s.neon}55`, textShadow: `0 0 10px ${s.neon}, 0 0 22px ${s.neon}88`,
        }}
      >
        {s.mono}
      </span>
      <span className="text-sm font-display font-bold uppercase tracking-wide text-[#F0F6FF]">{s.name}</span>
      <span
        className="absolute top-2.5 right-2.5 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md"
        style={{
          background: s.type === "free" ? "rgba(0,255,102,.14)" : "rgba(0,217,255,.12)",
          color: s.type === "free" ? "#00FF66" : "#88A0C0",
          border: s.type === "free" ? "1px solid rgba(0,255,102,.4)" : "1px solid rgba(0,217,255,.25)",
          textShadow: s.type === "free" ? "0 0 8px rgba(0,255,102,.6)" : "none",
        }}
      >
        {s.type === "free" ? "Free" : "Account"}
      </span>
    </button>
  );
}

export default function StreamingServices({ onStart, canStart }) {
  const [active, setActive] = useState(null);
  const [url, setUrl] = useState("");

  const open = (s) => {
    setActive(s);
    setUrl(s.type === "free" && s.home ? s.home : "");
  };
  const close = () => setActive(null);

  const start = () => {
    let u = (url || active.home || "").trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = "https://" + u;
    onStart(u);
    close();
  };

  const isPaid = active && active.type === "paid";
  const showExt = active && NEEDS_EXTENSION.has(active.key);
  const accent = active?.neon || "#00D9FF";

  return (
    <div className="w-full max-w-3xl mx-auto px-4" data-testid="streaming-services">
      <div className="text-center mb-5">
        <p className="font-display font-bold text-lg uppercase tracking-wider neon-cyan">Start from a streaming service</p>
        <p className="text-[#88A0C0] text-sm mt-1">
          {canStart ? "Pick a service, paste a video link, and watch in sync." : "Ask the driver to pick a service to start."}
        </p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
        {SERVICES.map((s) => <Tile key={s.key} s={s} onClick={open} />)}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="glass-cyber border-0 text-[#F0F6FF]" data-testid="service-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-xl uppercase tracking-wide">
                  <span className="flex items-center justify-center rounded-lg font-black" style={{ width: 32, height: 32, background: `${accent}18`, color: accent, fontSize: 16, border: `1px solid ${accent}55`, textShadow: `0 0 10px ${accent}` }}>
                    {active.mono.length > 2 ? active.mono[0].toUpperCase() : active.mono}
                  </span>
                  {active.name}
                </DialogTitle>
                <DialogDescription className="text-[#88A0C0] pt-1">
                  {isPaid
                    ? `Open ${active.name} in a new tab, come back and paste the video URL to sync playback with your room.`
                    : `${active.name} loads right inside Ibeflix. Paste a video URL${active.key === "youtube" ? "" : " (or use the homepage)"} and start watching together.`}
                </DialogDescription>
              </DialogHeader>

              {showExt && (
                <a
                  href="/ibeflix-extension.zip"
                  download
                  data-testid="service-extension-banner"
                  className="flex items-center gap-3 rounded-xl border border-[#00D9FF]/30 bg-[#00D9FF]/10 p-3 hover:bg-[#00D9FF]/15 transition-colors"
                >
                  <span className="w-9 h-9 rounded-lg bg-[#00D9FF]/20 flex items-center justify-center shrink-0">
                    <Chrome className="w-5 h-5 text-[#00D9FF]" />
                  </span>
                  <span className="text-left">
                    <span className="block text-sm font-semibold text-[#F0F6FF]">Get the Chrome Extension</span>
                    <span className="block text-xs text-[#88A0C0]">Keeps {active.name} playback in sync automatically. Tap to download.</span>
                  </span>
                </a>
              )}

              <div className="space-y-2">
                {isPaid && (
                  <a
                    href={active.home}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-open-tab"
                    className="btn-neon flex items-center justify-center gap-2 h-11 rounded-full bg-white/5 border border-[#00D9FF]/30 text-[#F0F6FF] font-semibold hover:border-[#00D9FF] hover:text-[#00D9FF] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Open {active.name} in a new tab
                  </a>
                )}
                <input
                  data-testid="service-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && canStart) start(); }}
                  placeholder={`Paste the ${active.name} video URL…`}
                  className="input-neon w-full h-11 bg-[#060817]/80 border border-[#00D9FF]/25 rounded-full px-4 text-sm text-[#F0F6FF] outline-none transition-colors"
                />
                <button
                  data-testid="start-watching-button"
                  onClick={start}
                  disabled={!canStart}
                  className="btn-neon glow-cyan w-full h-11 rounded-full bg-[#00D9FF] text-navy-ink font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {canStart ? <><Play className="w-4 h-4 fill-current" /> Start Watching Together</> : <><Lock className="w-4 h-4" /> Driver only</>}
                </button>
                {isPaid && (
                  <p className="flex items-center gap-1.5 text-xs text-[#88A0C0] justify-center pt-1">
                    <Radio className="w-3 h-3" /> Paid services block embedding — the URL syncs; use the extension to mirror playback.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
